export const HANDBOOK_COUNT_EVENTS = [
  'handbook_ad_view',
  'handbook_ad_click',
  'handbook_breed_select',
  'handbook_product_view',
  'handbook_personalize_click',
  'handbook_signup_prompt',
  'handbook_personalize_start',
  'handbook_personalize_complete',
  'handbook_step_back',
  'handbook_billing_invalid',
  'handbook_email_code_sent',
  'handbook_email_verified',
  'handbook_checkout_start',
  'handbook_checkout_progress',
  'handbook_pay_press',
  'handbook_payment_error',
  'handbook_checkout_error',
  'handbook_payment_complete',
  'handbook_purchase',
  'handbook_purchase_declined',
  'handbook_purchase_pending',
  'handbook_confirm_error',
  'handbook_open_click',
  'handbook_owned_view',
  'handbook_pdf_download_start',
  'handbook_pdf_download_complete',
  'handbook_pdf_download_error',
  'handbook_edit_answers_start',
  'handbook_edit_answers_cancel',
  'handbook_answers_update',
  'handbook_answers_update_error',
  'handbook_fit_start',
  'handbook_fit_complete',
  'handbook_fit_error',
  'handbook_hesitation',
  'handbook_early_reader_claim',
  'handbook_early_reader_error',
  'handbook_feedback_submit',
  'handbook_feedback_error'
] as const;

export type HandbookCountEvent = (typeof HANDBOOK_COUNT_EVENTS)[number];

export const HANDBOOK_COUNT_SURFACES = [
  'breed_page',
  'guides_hub',
  'waitlist_email',
  'waitlist_followup_email',
  'broadcast_email',
  'paid_search',
  'paid_search_google',
  'paid_search_bing',
  'search_page',
  'waitlist_widget',
  'dashboard',
  'messages',
  'research',
  'product_page',
  'purchase_success',
  'checkout'
] as const;

export const WAITLIST_FOLLOWUP_CAMPAIGN = 'waitlist_followup';

export const HANDBOOK_BILLING_FIELDS = [
  'line1',
  'city',
  'province',
  'postalCode',
  'firstName',
  'email'
] as const;

export const HANDBOOK_CTA_POSITIONS = [
  'hero',
  'footer',
  'sticky',
  'fit'
] as const;

export const HANDBOOK_HESITATION_REASONS = [
  'unsure_contents',
  'price',
  'already_know',
  'not_ready',
  'just_looking',
  'payment_method',
  'account'
] as const;

export const HANDBOOK_EARLY_READER_DETAIL = 'early_reader';

export const HANDBOOK_SIGNED_STATES = ['signed_in', 'signed_out'] as const;

export const HANDBOOK_CHECKOUT_PROGRESS_STEPS = [
  'postal_code',
  'payment_started',
  'payment_ready'
] as const;

export type HandbookCheckoutProgressStep =
  (typeof HANDBOOK_CHECKOUT_PROGRESS_STEPS)[number];

export const HANDBOOK_PAYMENT_ERROR_TYPES = [
  'validation_error',
  'card_error',
  'invalid_request_error',
  'api_connection_error',
  'api_error',
  'authentication_error',
  'rate_limit_error',
  'other'
] as const;

export type HandbookPaymentErrorType =
  (typeof HANDBOOK_PAYMENT_ERROR_TYPES)[number];

export const toHandbookPaymentErrorType = (
  type: string | undefined
): HandbookPaymentErrorType =>
  (HANDBOOK_PAYMENT_ERROR_TYPES as readonly string[]).includes(type ?? '')
    ? (type as HandbookPaymentErrorType)
    : 'other';

export const HANDBOOK_ANSWER_SECTIONS = [
  'alone',
  'activity',
  'location',
  'past_dogs'
] as const;

export type HandbookAnswerSection = (typeof HANDBOOK_ANSWER_SECTIONS)[number];

export type HandbookHesitationReason =
  (typeof HANDBOOK_HESITATION_REASONS)[number];

export const HANDBOOK_FEEDBACK_RATINGS = ['1', '2', '3', '4', '5'] as const;

export type HandbookCtaPosition = (typeof HANDBOOK_CTA_POSITIONS)[number];

export type HandbookCount = {
  event: HandbookCountEvent;
  breedSlug: string;
  surface: string;
  detail: string;
  staff: boolean;
};

const BREED_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const HTTP_STATUS_PATTERN = /^[1-5]\d{2}$/;
const POSITIONS: string[] = ['', ...HANDBOOK_CTA_POSITIONS];

const isErrorReason = (detail: string) =>
  detail === 'no_response' || HTTP_STATUS_PATTERN.test(detail);

const isPlainOrEarlyReader = (detail: string) =>
  detail === '' || detail === HANDBOOK_EARLY_READER_DETAIL;

const isBillingFieldList = (detail: string) => {
  const fields = detail.split(',');
  return (
    fields.length > 0 &&
    new Set(fields).size === fields.length &&
    fields.every((field) =>
      (HANDBOOK_BILLING_FIELDS as readonly string[]).includes(field)
    )
  );
};

const DETAIL_RULES: Record<HandbookCountEvent, (detail: string) => boolean> = {
  handbook_ad_view: (detail) => ['', 'owned', 'early_reader'].includes(detail),
  handbook_ad_click: (detail) =>
    ['preview', 'open', 'browse', 'claim'].includes(detail),
  handbook_breed_select: (detail) => detail === '',
  handbook_product_view: (detail) =>
    ['signed_in', 'signed_out'].includes(detail),
  handbook_personalize_click: (detail) => POSITIONS.includes(detail),
  handbook_signup_prompt: (detail) => POSITIONS.includes(detail),
  handbook_personalize_start: (detail) =>
    ['direct', 'auth_return', 'account', HANDBOOK_EARLY_READER_DETAIL].includes(
      detail
    ),
  handbook_personalize_complete: isPlainOrEarlyReader,
  handbook_step_back: (detail) => ['1', '2', '3'].includes(detail),
  handbook_billing_invalid: isBillingFieldList,
  handbook_email_code_sent: (detail) => detail === '',
  handbook_email_verified: (detail) => detail === '',
  handbook_checkout_start: (detail) =>
    detail === '' ||
    detail === 'additional_breed' ||
    (HANDBOOK_SIGNED_STATES as readonly string[]).includes(detail),
  handbook_checkout_progress: (detail) =>
    (HANDBOOK_CHECKOUT_PROGRESS_STEPS as readonly string[]).includes(detail),
  handbook_pay_press: (detail) =>
    (HANDBOOK_SIGNED_STATES as readonly string[]).includes(detail),
  handbook_payment_error: (detail) =>
    (HANDBOOK_PAYMENT_ERROR_TYPES as readonly string[]).includes(detail),
  handbook_checkout_error: isErrorReason,
  handbook_payment_complete: (detail) => detail === '',
  handbook_purchase: (detail) => detail === '' || detail === 'additional_breed',
  handbook_purchase_declined: (detail) => detail === 'residency',
  handbook_purchase_pending: (detail) => detail === '',
  handbook_confirm_error: isErrorReason,
  handbook_open_click: (detail) => POSITIONS.includes(detail),
  handbook_owned_view: isPlainOrEarlyReader,
  handbook_pdf_download_start: isPlainOrEarlyReader,
  handbook_pdf_download_complete: isPlainOrEarlyReader,
  handbook_pdf_download_error: (detail) => detail === '',
  handbook_edit_answers_start: (detail) => detail === '',
  handbook_edit_answers_cancel: (detail) => detail === '',
  handbook_answers_update: (detail) =>
    detail === '' ||
    (HANDBOOK_ANSWER_SECTIONS as readonly string[]).includes(detail),
  handbook_answers_update_error: isErrorReason,
  handbook_fit_start: (detail) => detail === '',
  handbook_fit_complete: (detail) => detail === '',
  handbook_fit_error: isErrorReason,
  handbook_hesitation: (detail) =>
    (HANDBOOK_HESITATION_REASONS as readonly string[]).includes(detail),
  handbook_early_reader_claim: (detail) => detail === '',
  handbook_early_reader_error: isErrorReason,
  handbook_feedback_submit: (detail) =>
    (HANDBOOK_FEEDBACK_RATINGS as readonly string[]).includes(detail),
  handbook_feedback_error: isErrorReason
};

export const isHandbookCountEvent = (
  value: string
): value is HandbookCountEvent =>
  (HANDBOOK_COUNT_EVENTS as readonly string[]).includes(value);

export const isValidHandbookCount = (count: {
  event: string;
  breedSlug: string;
  surface: string;
  detail: string;
}): count is Omit<HandbookCount, 'staff'> =>
  isHandbookCountEvent(count.event) &&
  (count.breedSlug === '' ||
    (count.breedSlug.length <= 80 &&
      BREED_SLUG_PATTERN.test(count.breedSlug))) &&
  (count.surface === '' ||
    (HANDBOOK_COUNT_SURFACES as readonly string[]).includes(count.surface)) &&
  DETAIL_RULES[count.event](count.detail);
